import React, { useEffect } from 'react';
import { Phone, Mail, MapPin, Calculator, Users, FileText, Award, Briefcase, MessageCircle, CheckCircle, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import TaxCalculator from '../components/TaxCalculator';
import LogosCarousel from '../components/LogosCarousel';
import SEO from '../components/SEO';

const Home = () => {
  const { toast } = useToast();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-in-section').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleWhatsApp = () => {
    const message = encodeURIComponent('Hola Cristian, me interesa conocer más sobre tus servicios de asesoría contable y tributaria.');
    window.open(`https://wa.me/56984174768?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO />
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-all duration-300">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_cristian-contador/artifacts/hw3twung_LOGO.png"
              alt="CVA Logo"
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />

            <span className="text-base sm:text-xl font-bold text-black hidden xs:inline">Cristian Vásquez Aburto</span>
            <span className="text-base font-bold text-black xs:hidden">CVA</span>
          </div>
          <nav className="hidden lg:flex space-x-6">
            <a href="#inicio" className="text-slate-700 hover:text-[#ef4444] transition-colors font-medium">Inicio</a>
            <a href="#sobre-mi" className="text-slate-700 hover:text-[#ef4444] transition-colors font-medium">Sobre Mí</a>
            <a href="#servicios" className="text-slate-700 hover:text-[#ef4444] transition-colors font-medium">Servicios</a>
            <a href="#formacion" className="text-slate-700 hover:text-[#ef4444] transition-colors font-medium">Formación</a>
            <a href="#contacto" className="text-slate-700 hover:text-[#ef4444] transition-colors font-medium">Contacto</a>
          </nav>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <a
              href="https://www.instagram.com/cristian.vasquez.contador/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-700 hover:text-[#ef4444] transition-all duration-300 hover:scale-110"
              aria-label="Instagram">

              <Instagram className="h-5 w-5" />
            </a>
            <Button
              onClick={handleWhatsApp}
              data-testid="header-whatsapp-btn"
              className="bg-[#ef4444] hover:bg-[#dc2626] text-white transition-all duration-300 hover:scale-105 text-sm sm:text-base px-3 sm:px-4">

              <MessageCircle className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">WhatsApp</span>
              <span className="sm:hidden">Chat</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-screen pt-20 pb-16 sm:pt-24 sm:pb-20 overflow-hidden flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black"></div>
          <img
            src="https://images.unsplash.com/photo-1762427354051-a9bdb181ae3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50aW5nJTIwb2ZmaWNlfGVufDB8fHx8MTc3MDA2NzI5Mnww&ixlib=rb-4.1.0&q=85"
            alt="Background"
            className="w-full h-full object-cover opacity-10" />
          {/* Gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="text-white space-y-4 sm:space-y-6 animate-fade-in text-center lg:text-left order-2 lg:order-1">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Tranquilidad Contable y Tributaria para que tu Negocio 
                <span className="text-[#ef4444]"> no se Detenga</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-200 leading-relaxed max-w-xl mx-auto lg:mx-0">
                <strong className="text-white">Cristian Vásquez Aburto</strong>  – Te asesoramos con total transparencia para que tomes la decisión correcta dentro de tu empresa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center lg:justify-start">
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  data-testid="hero-cta-asesoria"
                  className="btn-glow bg-[#ef4444] hover:bg-[#dc2626] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-bold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(239,68,68,0.5)] uppercase">
                  SOLICITA TU ASESORÍA
                </Button>
                <Button
                  onClick={() => document.getElementById('servicios').scrollIntoView({ behavior: 'smooth' })}
                  size="lg"
                  variant="outline"
                  data-testid="hero-cta-servicios"
                  className="btn-glow border-2 border-white text-white hover:bg-white hover:text-black px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-bold tracking-wide transition-all duration-300 hover:scale-105 uppercase">
                  VER SERVICIOS
                </Button>
              </div>
            </div>
            
            {/* Hero Image - Improved circular design with effects */}
            <div className="flex justify-center items-center relative order-1 lg:order-2">
              {/* Animated background effects */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-[280px] sm:w-[400px] md:w-[500px] h-[280px] sm:h-[400px] md:h-[500px] rounded-full bg-gradient-to-r from-[#ef4444] to-black opacity-20 blur-3xl animate-pulse"></div>
                <div className="absolute w-[320px] sm:w-[500px] md:w-[600px] h-[320px] sm:h-[500px] md:h-[600px] rounded-full border-2 sm:border-4 border-[#ef4444] opacity-10 animate-spin-slow"></div>
                <div className="absolute w-[300px] sm:w-[450px] md:w-[550px] h-[300px] sm:h-[450px] md:h-[550px] rounded-full border border-white/20 animate-spin-slower"></div>
              </div>
              
              {/* Glow effect behind image */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[300px] sm:w-[420px] md:w-[520px] h-[300px] sm:h-[420px] md:h-[520px] bg-gradient-to-br from-[#ef4444] via-black to-[#ef4444] opacity-40 blur-[60px] sm:blur-[90px] animate-pulse rounded-full"></div>
              </div>
              
              {/* Main circular image container */}
              <div className="relative z-10 w-full max-w-[260px] sm:max-w-[350px] md:max-w-[420px] lg:max-w-[480px] mx-auto">
                <div className="relative w-full aspect-square group cursor-pointer hero-image-glow">
                  {/* Outer glow rings */}
                  <div className="absolute inset-[-8px] sm:inset-[-12px] rounded-full bg-gradient-to-br from-[#ef4444] via-black to-[#ef4444] opacity-30 blur-xl animate-glow-pulse"></div>
                  <div className="absolute inset-[-4px] sm:inset-[-6px] rounded-full border-2 sm:border-4 border-[#ef4444]/40 blur-sm group-hover:border-[#ef4444]/60 transition-all duration-500"></div>
                  
                  {/* Image container with perfect circle */}
                  <div className="relative w-full h-full rounded-full overflow-hidden shadow-2xl ring-2 sm:ring-4 ring-[#ef4444]/30 ring-offset-2 sm:ring-offset-4 ring-offset-black group-hover:ring-[#ef4444]/60 group-hover:ring-4 sm:group-hover:ring-8 transition-all duration-500 animate-float">
                    <img
                      src="https://customer-assets.emergentagent.com/job_cristian-contador/artifacts/dvmwtzc5_Dise%C3%B1o%20sin%20t%C3%ADtulo.png"
                      alt="Cristian Vásquez Aburto - Contador Profesional"
                      className="w-full h-full object-cover object-top scale-105 transition-transform duration-700 ease-out group-hover:scale-115"
                      style={{
                        filter: 'brightness(1.1) contrast(1.15) saturate(1.05)',
                      }}
                    />
                    {/* Subtle overlay for depth */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                  </div>
                  
                  {/* Animated border glow */}
                  <div className="absolute inset-[-2px] rounded-full animate-shimmer pointer-events-none"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce-subtle hidden sm:block">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#ef4444] rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section id="sobre-mi" className="py-12 sm:py-16 lg:py-20 fade-in-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-6 sm:mb-8">SOBRE MÍ</h2>
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 lg:p-12 transition-all duration-300 hover:shadow-xl border-t-4 border-[#ef4444] card-hover-lift">
              <Award className="h-12 w-12 sm:h-16 sm:w-16 text-[#ef4444] mx-auto mb-4 sm:mb-6" />
              <p className="font-semibold text-lg sm:text-xl mb-4 text-black">CONTADOR ESPECIALISTA EN TRIBUTACIÓN</p>
              <p className="text-base sm:text-lg text-justify mb-6 text-slate-700">
                Trabajo con transparencia y lealtad, entregando asesoría confiable para cada decisión de tu empresa.
              </p>
              <p className="text-base sm:text-lg text-justify text-slate-700">
                Especialista en procesos de outsourcing, análisis financiero y cumplimiento normativo para empresas en Chile. <strong>Universidad de Los Lagos,</strong> con un 
                <strong> Diplomado en Gestión Tributaria en Thomson Reuters</strong> y un 
                <strong> Diplomado de Tributación Nacional en la Pontificia Universidad Católica de Valparaíso.</strong>
              </p>
              <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-10">
                <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                  <Briefcase className="h-8 w-8 sm:h-10 sm:w-10 text-black" />
                  <span className="font-semibold text-black text-sm sm:text-base">+5 Años</span>
                  <span className="text-slate-600 text-xs sm:text-sm">Experiencia</span>
                </div>
                <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                  <Users className="h-8 w-8 sm:h-10 sm:w-10 text-black" />
                  <span className="font-semibold text-black text-sm sm:text-base">50+ Clientes</span>
                  <span className="text-slate-600 text-xs sm:text-sm">Satisfechos</span>
                </div>
                <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                  <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-black" />
                  <span className="font-semibold text-black text-sm sm:text-base">100%</span>
                  <span className="text-slate-600 text-xs sm:text-sm">Transparencia</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-12 sm:py-16 lg:py-20 bg-zinc-50 fade-in-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">SERVICIOS PROFESIONALES</h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto">
              Soluciones contables integrales para tu empresa.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#ef4444] card-hover-lift">
              <CardContent className="p-6 sm:p-8 space-y-4">
                <div className="bg-black w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center group-hover:bg-[#ef4444] transition-colors duration-300">
                  <Calculator className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-black">CONTABILIDAD GENERAL</h3>
                <ul className="space-y-2 text-slate-700 text-sm sm:text-base">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Balances y estados financieros.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Orden administrativo completo.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Conciliación bancaria.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Libros auxiliares actualizados.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#ef4444] card-hover-lift">
              <CardContent className="p-6 sm:p-8 space-y-4">
                <div className="bg-black w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center group-hover:bg-[#ef4444] transition-colors duration-300">
                  <Users className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-black">GESTIÓN LABORAL</h3>
                <ul className="space-y-2 text-slate-700 text-sm sm:text-base">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Liquidación de sueldo.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Declaraciones juradas.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Registro Electrónico Laboral.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Gestión de documentación de personal.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#ef4444] card-hover-lift sm:col-span-2 lg:col-span-1">
              <CardContent className="p-6 sm:p-8 space-y-4">
                <div className="bg-black w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center group-hover:bg-[#ef4444] transition-colors duration-300">
                  <FileText className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-black">ASESORÍA TRIBUTARIA</h3>
                <ul className="space-y-2 text-slate-700 text-sm sm:text-base">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Planificación fiscal.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Gestión de impuestos.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Cierres de ejercicio.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-[#ef4444] mr-2 mt-0.5 flex-shrink-0" />
                    <span>Optimización tributaria.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tax Calculator Section */}
      <TaxCalculator />

      {/* Companies Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white fade-in-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">EMPRESAS QUE CONFÍAN EN MÍ</h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto">
              Experiencia comprobada con empresas líderes en la región.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <LogosCarousel />
          </div>
        </div>
      </section>

      {/* Academic Formation Section */}
      <section id="formacion" className="py-12 sm:py-16 lg:py-20 fade-in-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">FORMACIÓN ACADÉMICA</h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto">
              Educación profesional especializada en contabilidad y tributación.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
            <Card className="hover:shadow-2xl transition-all duration-300 border-l-4 border-[#ef4444] bg-white card-hover-lift">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="bg-black w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 sm:h-9 sm:w-9 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-2">DIPLOMADO EN GESTIÓN TRIBUTARIA</h3>
                    <p className="text-[#ef4444] font-semibold text-base sm:text-lg mb-2">Thomson Reuters</p>
                    <p className="text-slate-600 text-sm sm:text-base">
                      Especialización avanzada en gestión y planificación tributaria empresarial.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 border-l-4 border-[#ef4444] bg-white card-hover-lift">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="bg-black w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 sm:h-9 sm:w-9 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-2">DIPLOMADO DE TRIBUTACIÓN NACIONAL</h3>
                    <p className="text-[#ef4444] font-semibold text-base sm:text-lg mb-2">Pontificia Universidad Católica de Valparaíso</p>
                    <p className="text-slate-600 text-sm sm:text-base">
                      Formación especializada en normativa tributaria nacional y sus aplicaciones prácticas.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-zinc-50 fade-in-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">LO QUE DICEN NUESTROS CLIENTES</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
            {
              name: "Carolina Heinrich",
              company: "Turismo y Gastronomía Los Ríos SpA",
              text: "Venimos de malas experiencias y con Cristian encontramos orden y tranquilidad. Es un profesional en quien realmente se puede confiar."
            },
            {
              name: "Christian Puschel",
              company: "Comercial Food Fresh SpA",
              text: "Con Cristian por fin entendemos nuestra contabilidad. Siempre se da el tiempo de explicar y eso marca una gran diferencia."
            },
            {
              name: "Daniela Díaz",
              company: "Fascino del Caffe SpA",
              text: "Cristian se involucra con el negocio como si fuera propio. Su apoyo nos ha dado mucha seguridad para seguir creciendo."
            }].
            map((testimonial, index) =>
            <Card key={index} className="hover:shadow-xl transition-all duration-300 card-hover-lift">
                <CardContent className="p-6 sm:p-8 space-y-4">
                  <div className="flex space-x-1 mb-4">
                    {[...Array(5)].map((_, i) =>
                  <span key={i} className="text-[#ef4444] text-lg sm:text-xl">★</span>
                  )}
                  </div>
                  <p className="text-slate-700 italic text-sm sm:text-base">&ldquo;{testimonial.text}&rdquo;</p>
                  <div className="pt-4 border-t">
                    <p className="font-bold text-black text-sm sm:text-base">{testimonial.name}</p>
                    <p className="text-xs sm:text-sm text-slate-600">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-12 sm:py-16 lg:py-20 fade-in-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">CONTÁCTANOS</h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600">Estamos aquí para ayudarte con tus necesidades contables.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 max-w-6xl mx-auto">
            <div className="space-y-6 sm:space-y-8">
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-4 sm:mb-6">INFORMACIÓN DE CONTACTO</h3>
              
              <div className="flex items-start space-x-4">
                <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-[#ef4444] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-black text-sm sm:text-base">Ubicación</p>
                  <p className="text-slate-700 text-sm sm:text-base">Bilbao 1174 Oficina 5, Osorno, Chile</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-[#ef4444] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-black text-sm sm:text-base">Teléfono</p>
                  <p className="text-slate-700 text-sm sm:text-base">+569 84174768</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-[#ef4444] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-black text-sm sm:text-base">Email</p>
                  <p className="text-slate-700 text-sm sm:text-base">contacto@cristianvasquezcontador.cl</p>
                </div>
              </div>

              <div className="pt-4 sm:pt-6">
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  data-testid="contact-whatsapp-btn"
                  className="w-full bg-[#25D366] hover:bg-[#1fb855] text-white transition-all duration-300 hover:scale-105 text-base sm:text-lg py-5 sm:py-6 btn-glow">

                  <MessageCircle className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
                  CONTÁCTANOS POR WHATSAPP
                </Button>
              </div>

              <div className="pt-2 sm:pt-4">
                <a
                  href="https://www.instagram.com/cristian.vasquez.contador/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 text-slate-700 hover:text-[#ef4444] transition-all duration-300 group">

                  <Instagram className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm sm:text-base">@cristian.vasquez.contador</span>
                </a>
              </div>
            </div>

            <Card className="shadow-xl border-t-4 border-[#ef4444] overflow-hidden">
              <CardContent className="p-0">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3088.123456!2d-73.1355!3d-40.5731!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9610972e5c0c46c3%3A0x7b1d3a8e0f2c5d4e!2sBilbao%201174%2C%20Osorno%2C%20Los%20Lagos!5e0!3m2!1ses!2scl!4v1707087600000!5m2!1ses!2scl"
                  width="100%"
                  height="350"
                  style={{ border: 0, minHeight: '300px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Oficina - Bilbao 1174 Oficina 5, Osorno"
                  className="w-full sm:h-[400px] lg:h-[450px]">
                </iframe>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-8 sm:py-10 lg:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_cristian-contador/artifacts/hw3twung_LOGO.png"
                  alt="CVA Logo"
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain brightness-0 invert" />

                <span className="text-lg sm:text-xl font-bold">CVA Contabilidad</span>
              </div>
              <p className="text-slate-300 text-sm sm:text-base">
                Servicios contables profesionales con transparencia y excelencia.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-base sm:text-lg mb-4">Servicios</h4>
              <ul className="space-y-2 text-slate-300 text-sm sm:text-base">
                <li>Asesoría Contable</li>
                <li>Gestión Laboral</li>
                <li>Asesoría Tributaria</li>
                <li>Análisis Financiero</li>
              </ul>
            </div>
            
            <div className="sm:col-span-2 lg:col-span-1">
              <h4 className="font-bold text-base sm:text-lg mb-4">Contacto</h4>
              <ul className="space-y-2 text-slate-300 text-sm sm:text-base">
                <li>Bilbao 1174 Oficina 5, Osorno, Chile</li>
                <li>+569 84174768</li>
                <li>contacto@cristianvasquezcontador.cl</li>
              </ul>
              <div className="mt-4">
                <a
                  href="https://www.instagram.com/cristian.vasquez.contador/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-slate-300 hover:text-[#ef4444] transition-all duration-300 group">

                  <Instagram className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm sm:text-base">@cristian.vasquez.contador</span>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400 text-sm sm:text-base">
            <p>&copy; 2024 Cristian Vásquez Aburto. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#1fb855] text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 animate-bounce"
        aria-label="Contactar por WhatsApp">

        <MessageCircle className="h-8 w-8" />
      </button>
    </div>);

};

export default Home;